import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Award, Dumbbell, Shield, Star, MapPin, Clock, Search, ArrowLeft,
  CalendarDays, Users, DollarSign, ChevronRight, Loader2, ArrowUpDown, Zap
} from "lucide-react";

const ROLE_ICONS = { coach: Award, trainer: Dumbbell, official: Shield };
const ROLE_COLORS = { coach: "bg-blue-100 text-blue-700", trainer: "bg-red-100 text-red-700", official: "bg-purple-100 text-purple-700" };

export default function FindCoachPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("coaches");

  // Coaches state
  const [coaches, setCoaches] = useState([]);
  const [coachLoading, setCoachLoading] = useState(true);
  const [cQuery, setCQuery] = useState("");
  const [cSport, setCSport] = useState("");
  const [cCity, setCCity] = useState("");
  const [cMinRating, setCMinRating] = useState("");
  const [cMaxPrice, setCMaxPrice] = useState("");
  const [cSort, setCSort] = useState("rating");

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [sessLoading, setSessLoading] = useState(true);
  const [sQuery, setSQuery] = useState("");
  const [sSport, setSSport] = useState("");
  const [sCity, setSCity] = useState("");
  const [sMaxPrice, setSMaxPrice] = useState("");
  const [sSort, setSSort] = useState("date");

  useEffect(() => { searchCoaches(); searchSessions(); }, []);

  const searchCoaches = async () => {
    setCoachLoading(true);
    try {
      const params = new URLSearchParams();
      if (cQuery) params.set("q", cQuery);
      if (cSport) params.set("sport", cSport);
      if (cCity) params.set("city", cCity);
      if (cMinRating) params.set("min_rating", cMinRating);
      if (cMaxPrice) params.set("max_price", cMaxPrice);
      params.set("sort_by", cSort);
      params.set("verified_only", "true");
      const res = await api.get(`/sp/search?${params}`);
      setCoaches(res.data.professionals || []);
    } catch { setCoaches([]); }
    setCoachLoading(false);
  };

  const searchSessions = async () => {
    setSessLoading(true);
    try {
      const params = new URLSearchParams();
      if (sSport) params.set("activity", sSport);
      params.set("status", "open");
      const res = await api.get(`/vigf/activity-sessions?${params}`);
      let items = res.data.sessions || [];
      // Also get coach sessions
      try {
        const cRes = await api.get("/sessions/browse");
        const coachSessions = (cRes.data || []).filter(s => s.status === "open");
        items = [...items, ...coachSessions.map(s => ({
          session_id: s.session_id, title: s.title, instructor_name: s.host_name,
          sport_or_activity: s.sport, venue_name: s.venue_name,
          session_price: s.price_amount, start_datetime: `${s.session_date}T${s.start_time}:00`,
          max_capacity: s.slot_limit, participants_count: s.participants_count || 0,
          skill_level: "OPEN", session_type: "GROUP", session_status: "open",
        }))];
      } catch {}
      setSessions(items);
    } catch { setSessions([]); }
    setSessLoading(false);
  };

  // Client-side filtering & sorting for sessions
  const filteredSessions = sessions.filter(s => {
    if (sQuery && !s.title?.toLowerCase().includes(sQuery.toLowerCase()) && !s.instructor_name?.toLowerCase().includes(sQuery.toLowerCase())) return false;
    if (sSport && s.sport_or_activity !== sSport && s.sport !== sSport) return false;
    if (sCity && !s.venue_name?.toLowerCase().includes(sCity.toLowerCase())) return false;
    if (sMaxPrice && s.session_price > parseFloat(sMaxPrice)) return false;
    return true;
  }).sort((a, b) => {
    if (sSort === "date") return (a.start_datetime || "").localeCompare(b.start_datetime || "");
    if (sSort === "price_low") return (a.session_price || 0) - (b.session_price || 0);
    if (sSort === "price_high") return (b.session_price || 0) - (a.session_price || 0);
    return 0;
  });

  // Client-side sorting for coaches
  const sortedCoaches = [...coaches].sort((a, b) => {
    if (cSort === "rating") return (b.rating || 0) - (a.rating || 0);
    if (cSort === "price") return (a.pricing?.per_hour || 0) - (b.pricing?.per_hour || 0);
    if (cSort === "sessions") return (b.total_sessions || 0) - (a.total_sessions || 0);
    if (cSort === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
    return 0;
  });

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6" data-testid="find-coach-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />Back
      </button>

      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F3D2E] mb-2">Train With a Coach</h1>
      <p className="text-muted-foreground mb-6">Find coaches, trainers, and sessions to level up your game.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md mb-6">
          <TabsTrigger value="coaches" className="flex items-center gap-1"><Award className="w-4 h-4" />Coaches & Trainers</TabsTrigger>
          <TabsTrigger value="sessions" className="flex items-center gap-1"><Zap className="w-4 h-4" />Events & Sessions</TabsTrigger>
        </TabsList>

        {/* ══════ COACHES TAB ══════ */}
        <TabsContent value="coaches" className="space-y-4">
          <div className="flex gap-2 flex-wrap items-end">
            <div className="flex-1 min-w-[180px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={cQuery} onChange={e => setCQuery(e.target.value)} placeholder="Search by name or sport..."
                className="pl-9" onKeyDown={e => e.key === "Enter" && searchCoaches()} data-testid="coach-search" />
            </div>
            <Select value={cSport} onValueChange={v => setCSport(v === "all" ? "" : v)}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Sport" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {["basketball","badminton","tennis","volleyball","football","fitness","yoga","dance","boxing"].map(s =>
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={cCity} onChange={e => setCCity(e.target.value)} placeholder="City" className="w-32" />
            <Input value={cMinRating} onChange={e => setCMinRating(e.target.value)} type="number" min="0" max="5" step="0.5" placeholder="Min Rating" className="w-28" />
            <Input value={cMaxPrice} onChange={e => setCMaxPrice(e.target.value)} type="number" min="0" placeholder="Max ₱/hr" className="w-28" />
            <Select value={cSort} onValueChange={v => { setCSort(v); searchCoaches(); }}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rating"><ArrowUpDown className="w-3 h-3 inline mr-1" />Top Rated</SelectItem>
                <SelectItem value="price"><ArrowUpDown className="w-3 h-3 inline mr-1" />Lowest Price</SelectItem>
                <SelectItem value="sessions"><ArrowUpDown className="w-3 h-3 inline mr-1" />Most Booked</SelectItem>
                <SelectItem value="newest"><ArrowUpDown className="w-3 h-3 inline mr-1" />Newest</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={searchCoaches} className="rounded-full"><Search className="w-4 h-4 mr-1" />Search</Button>
          </div>

          {coachLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : sortedCoaches.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed rounded-xl">
              <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="font-heading text-lg text-muted-foreground">No coaches found</p>
              <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{sortedCoaches.length} professional{sortedCoaches.length !== 1 ? "s" : ""}</p>
              {sortedCoaches.map(sp => (
                <Card key={sp.sp_id} className="cursor-pointer hover:border-primary/50 transition-all hover:-translate-y-0.5"
                  onClick={() => navigate(`/sp/view/${sp.sp_id}`)} data-testid={`coach-${sp.sp_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-heading text-base font-bold">{sp.name}</h3>
                          {sp.roles_enabled?.map(r => {
                            const I = ROLE_ICONS[r] || Award;
                            return <Badge key={r} className={`${ROLE_COLORS[r]} text-[9px]`}><I className="w-3 h-3 mr-0.5" />{r}</Badge>;
                          })}
                          {sp.verification_status === "verified" && <Badge className="bg-green-100 text-green-800 text-[9px]">Verified</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{sp.primary_sport} · {sp.years_experience} yrs exp</p>
                        {sp.bio && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{sp.bio}</p>}
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                          {sp.service_locations?.length > 0 && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{sp.service_locations.slice(0, 2).join(", ")}</span>}
                          <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-500" />{sp.rating || 0} ({sp.total_ratings})</span>
                          <span className="flex items-center gap-0.5"><CalendarDays className="w-3 h-3" />{sp.total_sessions} sessions</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        {sp.pricing?.per_hour && <p className="font-heading font-bold text-primary text-lg">₱{sp.pricing.per_hour}<span className="text-xs text-muted-foreground font-normal">/hr</span></p>}
                        <ChevronRight className="w-4 h-4 text-muted-foreground mt-1 ml-auto" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ══════ SESSIONS TAB ══════ */}
        <TabsContent value="sessions" className="space-y-4">
          <div className="flex gap-2 flex-wrap items-end">
            <div className="flex-1 min-w-[180px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={sQuery} onChange={e => setSQuery(e.target.value)} placeholder="Search sessions..."
                className="pl-9" data-testid="session-search" />
            </div>
            <Select value={sSport} onValueChange={v => setSSport(v === "all" ? "" : v)}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Sport" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {["basketball","badminton","tennis","volleyball","football","fitness","yoga","dance","boxing","hiphop","kpop","ballet","pilates","crossfit"].map(s =>
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={sCity} onChange={e => setSCity(e.target.value)} placeholder="Venue/City" className="w-32" />
            <Input value={sMaxPrice} onChange={e => setSMaxPrice(e.target.value)} type="number" min="0" placeholder="Max ₱" className="w-28" />
            <Select value={sSort} onValueChange={setSSort}>
              <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date"><ArrowUpDown className="w-3 h-3 inline mr-1" />Date (Soonest)</SelectItem>
                <SelectItem value="price_low"><ArrowUpDown className="w-3 h-3 inline mr-1" />Price (Low→High)</SelectItem>
                <SelectItem value="price_high"><ArrowUpDown className="w-3 h-3 inline mr-1" />Price (High→Low)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {sessLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed rounded-xl">
              <Zap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="font-heading text-lg text-muted-foreground">No sessions found</p>
              <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{filteredSessions.length} session{filteredSessions.length !== 1 ? "s" : ""}</p>
              {filteredSessions.map(s => (
                <Card key={s.session_id} className="cursor-pointer hover:border-primary/50 transition-all hover:-translate-y-0.5"
                  onClick={() => navigate(s.session_id?.startsWith("asess_") ? "#" : `/coach/sessions/${s.session_id}`)}
                  data-testid={`session-${s.session_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-medium">{s.title}</h3>
                          <Badge variant="outline" className="text-[9px] capitalize">{s.sport_or_activity || s.sport}</Badge>
                          {s.skill_level && s.skill_level !== "OPEN" && <Badge variant="outline" className="text-[9px]">{s.skill_level}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{s.instructor_name || s.host_name} · {s.venue_name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-0.5"><CalendarDays className="w-3 h-3" />{(s.start_datetime || s.session_date || "").slice(0, 10)}</span>
                          <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{(s.start_datetime || "").slice(11, 16) || s.start_time}</span>
                          <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{s.participants_count}/{s.max_capacity || s.slot_limit}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="font-heading font-bold text-primary text-lg">₱{(s.session_price || s.price_amount || 0).toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground">per session</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
